import {
  PathCreatorCohortsQuery,
  usePathCreatorCohortsQuery
} from "../../api/generated"
import ItemSelector, { ItemSelectorProps } from "./ItemSelector"
import { registrationRoleIntMap } from "./RoleBadge"
import { SelectedCohort } from "./state"

// Unfortunately we can't pass params to fragments, so typing the user_registration
// onto the PathCreatorCohortFragment is a tricky problem, hence using the cohort node.
type SelectedCohortNode = NonNullable<
  NonNullable<
    NonNullable<
      NonNullable<PathCreatorCohortsQuery["cohorts"]>["edges"]
    >[number]
  >["node"]
>

// Maps the PathCreatorCohortFragment to the SelectedCohort type.
export function massageCohort(
  // TODO: fix this manual typing. Unfortunately we can't pass params to
  // fragments, so typing the user_registration onto the PathCreatorCohortFragment
  // is a tricky problem.
  cohort: SelectedCohortNode
): SelectedCohort {
  return {
    id: cohort.id,
    name: cohort.name,
    // Map cohort path role to key (we filter out cohorts the user is registered for, so expecting null).
    role: registrationRoleIntMap.get(cohort.user_registration?.role!)
      ?.enum as SelectedCohort["role"],
    registrations: cohort.registrations!
  }
}

// Sort cohorts for which the user is registered to the bottom.
function sortCohorts(cohorts: SelectedCohort[]) {
  return cohorts.sort((a, b) => {
    if (a.role && !b.role) return 1 // a is registered, b is not
    if (!a.role && b.role) return -1 // a is not registered, b is
    return 0
  })
}

type CohortSelectorProps = Omit<
  ItemSelectorProps<SelectedCohort>,
  "options" | "isLoading" | "itemLabel" | "getNewOptionData"
> & {
  targetUserId: number
  selectedResourceId: number
}

function CohortSelector({
  value,
  targetUserId,
  selectedResourceId,
  ...itemSelectorProps
}: CohortSelectorProps) {
  const isNewResource = selectedResourceId === -1
  const query = usePathCreatorCohortsQuery({
    variables: {
      // There should always be a selected resource at this point, but TS doesn't now that
      resourceId: selectedResourceId!,
      targetUserId
    },
    skip: !selectedResourceId || isNewResource
  })
  const cohorts =
    query.data?.cohorts?.edges?.map((edge) => edge?.node!).map(massageCohort) ||
    []

  // Disable selecting cohorts where teh user is already registered.
  function isOptionDisabled(item: SelectedCohort) {
    return !!item.role
  }

  return (
    <ItemSelector
      itemLabel="Cohort"
      icon="group"
      value={value?.id ? value : null}
      options={sortCohorts(cohorts)}
      isLoading={query.loading}
      isOptionDisabled={isOptionDisabled}
      getNewOptionData={(inputValue, optionLabel) => ({
        id: -1,
        name: inputValue
      })}
      {...itemSelectorProps}
    />
  )
}

export default CohortSelector
